// =============================================================================
// SimSense Platform — Auto-Updater Hook
// =============================================================================
// Checks for updates on app launch (Tauri desktop only).
// Uses the Tauri updater plugin with signed manifests from releases.simsense.app.

import { useEffect, useState } from "react";
import { useTauri } from "./useTauri";
import { remoteLog } from "../utils/logger";

type UpdateStatus = "idle" | "checking" | "available" | "downloading" | "ready" | "error";

export function useUpdater() {
  const isTauri = useTauri();
  const [status, setStatus] = useState<UpdateStatus>("idle");
  const [version, setVersion] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isTauri) return;

    const checkForUpdates = async () => {
      try {
        setStatus("checking");
        const { check } = await import("@tauri-apps/plugin-updater");
        const update = await check();

        if (update) {
          setVersion(update.version);
          setStatus("available");
          void remoteLog("Update available", "info", { version: update.version });
        } else {
          setStatus("idle");
        }
      } catch (err) {
        setStatus("error");
        setError(String(err));
        void remoteLog("Update check failed", "warn", { error: String(err) });
      }
    };

    // Check after a short delay so the app loads first
    const timer = setTimeout(() => {
      void checkForUpdates();
    }, 5000);

    return () => clearTimeout(timer);
  }, [isTauri]);

  const installUpdate = async () => {
    try {
      setStatus("downloading");
      const { check } = await import("@tauri-apps/plugin-updater");
      const { relaunch } = await import("@tauri-apps/plugin-process");

      const update = await check();
      if (update) {
        await update.downloadAndInstall();
        setStatus("ready");
        void remoteLog("Update installed, relaunching", "info", { version: update.version });
        await relaunch();
      }
    } catch (err) {
      setStatus("error");
      setError(String(err));
      void remoteLog("Update install failed", "error", { error: String(err) });
    }
  };

  return { status, version, error, installUpdate };
}
